import type { CustomStatuses, UserPermissions } from "@services/user/types";
import { paths } from "@utils/navigation";

export type RoleConfig =
  | {
      redirectPath: string;
      requestSCLabelKey: string;
      customSecurityClearancePath: string | null;
      requestGatepassLabelKey: string;
      registerVesselLabelKey: string;
      accessPermissionsList: string[];
      actionPermissionsList: string[];
      customStatuses?: CustomStatuses;
      isMarineDepartmentUser?: boolean;
      isShippingAgentUser?: boolean;
      isOperationsUser?: boolean;
      isHSEUser?: boolean;
    }
  | ""
  | null;
const defineServiceDeskRole = (userPerm: UserPermissions) => {
  const { accessPermissionsList, actionPermissionsList } =
    getUserAccessAndActionPermissions(userPerm);

  return {
    redirectPath: `${paths.licenses?.index}`,
    requestSCLabelKey: "security-clearance.request-security-clearance",
    registerVesselLabelKey: "vessel-management.register-vessel",
    requestGatepassLabelKey: "gatepass.request-gatepass",
    customSecurityClearancePath: null,
    accessPermissionsList,
    actionPermissionsList: [
      ...actionPermissionsList,
      "canDownloadVendorLicenseCsv",
      "canPrintLicenseApplicationForm",
    ],
  };
};

const defineManagingDirectorOfficeRole = (userPerm: UserPermissions) => {
  const { accessPermissionsList, actionPermissionsList } =
    getUserAccessAndActionPermissions(userPerm);

  return {
    redirectPath: `${paths.licenses.portOperatorIndex}`,
    requestSCLabelKey: "security-clearance.request-security-clearance",
    registerVesselLabelKey: "vessel-management.register-vessel",
    requestGatepassLabelKey: "gatepass.request-gatepass",
    customSecurityClearancePath: null,
    accessPermissionsList,
    actionPermissionsList: [...actionPermissionsList, "canPrintLicenseApplicationForm"],
  };
};
const defineIndividualRole = (
  userPerm: UserPermissions,
  scID: string | null,
  scStatus: string | null,
) => {
  let { actionPermissionsList } = getUserAccessAndActionPermissions(userPerm);
  let accessPermissionsList: string[] = [];

  if (!scID) {
    accessPermissionsList = ["hasSecurityClearanceAccess"];
  } else if (scID && scStatus !== "APPROVED") {
    accessPermissionsList = ["hasSecurityClearanceRequestAccess"];
  } else if (scID && scStatus === "APPROVED") {
    accessPermissionsList = [
      "hasGatepassAccess",
      "hasSecurityClearanceAccess",
      "hasSecurityClearanceRequestAccess",
    ];
  }
  actionPermissionsList = [...actionPermissionsList, "canRenewSecurityClearanceOutsideClaim"];
  // Individuals can request a new SC when they have no active approved or
  // in-progress clearance. CLOSED and RENEWED are historical statuses, so
  // they must not prevent a new request when returned by the current-individual lookup.
  if (scID && !["CLOSED", "RENEWED"].includes(String(scStatus ?? "").toUpperCase())) {
    actionPermissionsList = filterActionPermissions(actionPermissionsList, [
      "canRequestSecurityClearance",
    ]);
  }

  return {
    // If user has no SC yet then land directly on the Check page to start a request.
    // If user already has a linked SC: APPROVED -> Gatepass; otherwise show the SC preview.
    redirectPath: scID
      ? scStatus === "APPROVED"
        ? `${paths.gatepass.index}`
        : `${paths.portSecurity.index}/${paths.portSecurity.securityClearanceRequestPreview}/${scID}`
      : `${paths.portSecurity.index}/${paths.portSecurity.requestSecurityClearance}/${paths.portSecurity.checkId}`,
    requestSCLabelKey: "security-clearance.request-security-clearance",
    registerVesselLabelKey: "vessel-management.register-vessel",
    requestGatepassLabelKey: "gatepass.request-gatepass",
    customSecurityClearancePath: scID
      ? `${paths.portSecurity.index}/${paths.portSecurity.securityClearanceRequestPreview}/${scID}`
      : null,
    accessPermissionsList,
    actionPermissionsList,
    customStatuses: {
      statusUpponSubmiting: "PENDING_SPONSOR_COMPANY_APPROVAL",
      statusUpponResubmiting: null,
      statusUpponApproving: null,
      statusUpponCancelling: null,
      statusUpponRejecting: null,
      statusUpponSuspending: null,
    },
  };
};

const defineSponsorCompanyRole = (userPerm: UserPermissions) => {
  const { accessPermissionsList, actionPermissionsList } =
    getUserAccessAndActionPermissions(userPerm);

  return {
    redirectPath: `${paths.portSecurity.index}`,
    requestSCLabelKey: "security-clearance.request-security-clearance",
    registerVesselLabelKey: "vessel-management.register-vessel",
    requestGatepassLabelKey: "gatepass.request-gatepass",
    customSecurityClearancePath: null,
    accessPermissionsList,
    actionPermissionsList: [...actionPermissionsList, "canAlwaysRequestGatepass"],
    customStatuses: {
      statusUpponSubmiting: "PENDING_SECURITY_DEPT_APPROVAL",
      statusUpponResubmiting: "PENDING_RESUBMISSION",
      statusUpponApproving: "PENDING_SECURITY_DEPT_APPROVAL",
      statusUpponCancelling: null,
      statusUpponRejecting: null,
      statusUpponSuspending: null,
    },
  };
};

const defineVendorMasterRole = (userPerm: UserPermissions) => {
  const { actionPermissionsList } = getUserAccessAndActionPermissions(userPerm);
  const { accessPermissionsList } = getUserAccessAndActionPermissions(userPerm);

  // Filter out permissions that vendors should not have
  const filteredActionPermissions = filterActionPermissions(actionPermissionsList, [
    "canApproveSecurityClearance",
    "canRejectSecurityClearance",
    "canResubmitSecurityClearance",
  ]);

  // Add claim/unclaim permissions for vendors to view their requests
  const vendorPermissions = [
    ...filteredActionPermissions,
    "canClaimSecurityClearance",
    "canUnclaimSecurityClearance",
    "canAlwaysRequestGatepass",
  ];

  return {
    redirectPath: `${paths.portSecurity.index}`,
    requestSCLabelKey: "security-clearance.request-security-clearance",
    registerVesselLabelKey: "vessel-management.register-vessel",
    requestGatepassLabelKey: "gatepass.request-gatepass",
    customSecurityClearancePath: null,

    accessPermissionsList,
    actionPermissionsList: vendorPermissions,
    customStatuses: {
      statusUpponSubmiting: "PENDING_SPONSOR_COMPANY_APPROVAL",
      statusUpponResubmiting: null,
      statusUpponApproving: null,
      statusUpponCancelling: null,
      statusUpponRejecting: null,
      statusUpponSuspending: null,
    },
  };
};

const defineSecurityOperatorRole = (userPerm: UserPermissions) => {
  const { accessPermissionsList, actionPermissionsList } =
    getUserAccessAndActionPermissions(userPerm);

  return {
    redirectPath: `${paths.portSecurity.index}`,
    requestSCLabelKey: "security-clearance.add-security-clearance",
    registerVesselLabelKey: "vessel-management.register-vessel",
    requestGatepassLabelKey: "gatepass.add-gatepass",
    customSecurityClearancePath: null,

    accessPermissionsList,
    actionPermissionsList: [
      ...actionPermissionsList,
      // Allow Security Operator to see gatepass grid checkboxes (for bulk claim)
      "hasGatepassCheckbox",
      // Allow Security Operator to see security clearance grid checkboxes (for bulk actions)
      "hasSecurityClearanceCheckbox",
      "canDirectlyApproveSecurityClearance",
      "canAlwaysRequestGatepass",
    ],
    customStatuses: {
      statusUpponSubmiting: "PENDING_SPONSOR_COMPANY_APPROVAL",
      statusUpponResubmiting: "PENDING_RESUBMISSION",
      statusUpponApproving: "PENDING_SECURITY_SUPERVISOR_APPROVAL",
      statusUpponRejecting: null,
      statusUpponCancelling: null,
      statusUpponSuspending: "SUSPENDED",
    },
  };
};

const defineSecuritySupervisorRole = (userPerm: UserPermissions) => {
  const { accessPermissionsList } = getUserAccessAndActionPermissions(userPerm);
  let { actionPermissionsList } = getUserAccessAndActionPermissions(userPerm);
  actionPermissionsList = [
    ...actionPermissionsList,
    "canChangeSecurityClearanceValidityPeriod",
    "canAlwaysRequestGatepass",
    "hasGatepassCheckbox",
    "hasSecurityClearanceCheckbox",
  ];

  return {
    redirectPath: `${paths.portSecurity.index}`,
    requestSCLabelKey: "security-clearance.add-security-clearance",
    registerVesselLabelKey: "vessel-management.register-vessel",
    requestGatepassLabelKey: "gatepass.add-gatepass",
    customSecurityClearancePath: null,

    accessPermissionsList,
    actionPermissionsList: [...actionPermissionsList, "canCancelSecurityClearance"],
    customStatuses: {
      statusUpponSubmiting: "PENDING_SPONSOR_COMPANY_APPROVAL",
      statusUpponResubmiting: "PENDING_RESUBMISSION",
      statusUpponApproving: "APPROVED",
      statusUpponCancelling: "CLOSED",
      statusUpponRejecting: "PERMANENTLY_REJECTED",
      statusUpponSuspending: "SUSPENDED",
    },
  };
};

const defineShippingAgentRole = (userPerm: UserPermissions) => {
  const { accessPermissionsList, actionPermissionsList } =
    getUserAccessAndActionPermissions(userPerm);

  return {
    redirectPath: `${paths.vesselCall.index}`,
    requestSCLabelKey: "security-clearance.add-security-clearance",
    registerVesselLabelKey: "vessel-management.register-vessel",
    requestGatepassLabelKey: "gatepass.request-gatepass",
    customSecurityClearancePath: null,
    accessPermissionsList,
    actionPermissionsList: [
      ...actionPermissionsList,
      "canEditVesselRegistration",
      "canRenewNoc",
      "canRequestDeparture",
      "canResubmitDeparture",
      "canRequestPortService",
      "canSubmitFinalVesselCall",
      "canCancelMovementService",
      "canClaimMovementService",
      "canCancelPortService",
      "canStartPortService",
      "canClosePortService",
      "canUpdatePortServices",
      "canStartPortServicesWasteDisposalGarbage",
      "canClosePortServicesWasteDisposalGarbage",
      "canStartPortServicesWasteDisposalSludge",
      "canRejectPortServicesWasteDisposalSludge",
      "canClosePortServicesWasteDisposalSludge",
      "canClaimShippingAgencyTransfer",
      "canUnclaimShippingAgencyTransfer",
      "canApproveShippingAgencyTransfer",
      "canRejectShippingAgencyTransfer",
      "canHandleVesselCallDraft",
      "canStartPortServicesWasteDisposalSlops",
      "canClosePortServicesWasteDisposalSlops",
      "canStartPortServicesHseWasteDisposalSlops",
      "canClosePortServicesHseWasteDisposalSlops",
      "canUpdatePortServicesWasteDisposalSlops",
      "canUpdatePortServicesHseSludge",
      "canUpdatePortServicesBunkering",
      "canStartPortServicesShipToShip",
      "canClosePortServicesShipToShip",
      "canSeeHyperlinkAssociatedPortService",
      "canRequestDisputeResolution",
    ],
    isShippingAgentUser: true,
  };
};

const defineHSERole = (userPerm: UserPermissions) => {
  const { accessPermissionsList, actionPermissionsList } =
    getUserAccessAndActionPermissions(userPerm);

  return {
    redirectPath: `${paths.portServices.index}`,
    requestSCLabelKey: "security-clearance.add-security-clearance",
    registerVesselLabelKey: "vessel-management.register-vessel",
    requestGatepassLabelKey: "gatepass.request-gatepass",
    customSecurityClearancePath: null,
    accessPermissionsList,
    actionPermissionsList: [
      ...actionPermissionsList,
      "canApprovePortServicesWasteDisposalSludge",
      "canRejectPortServicesWasteDisposalSludge",
      "canApprovePortServicesHseWasteDisposalSlops",
      "canRejectPortServicesHseWasteDisposalSlops",
      "canCancelPortService",
      "canRejectPortService",
    ],
    isHSEUser: true,
  };
};

const defineMarineOperatorRole = (userPerm: UserPermissions) => {
  const { accessPermissionsList, actionPermissionsList } =
    getUserAccessAndActionPermissions(userPerm);

  // Temporary fix until the BE refactor for submodules
  const filteredActionPermissionsList = actionPermissionsList.filter(
    (perm) => perm !== "canUpdateVesselCall" && perm !== "canResumeVesselCall",
  );

  return {
    redirectPath: `${paths.vesselManagement.index}`,
    requestSCLabelKey: "security-clearance.add-security-clearance",
    registerVesselLabelKey: "vessel-management.register-vessel",
    requestGatepassLabelKey: "gatepass.request-gatepass",
    accessPermissionsList: [
      ...accessPermissionsList,
      "hasTaskAccess",
      "hasChangeVesselStatusAccess",
      "hasInternalNotesAccess",
      "hasShippingAgencyTransferAccess",
    ],
    customSecurityClearancePath: null,
    actionPermissionsList: [
      ...filteredActionPermissionsList,
      "canCloseMovementService",
      "canCancelMovementService",
      "canRejectMovementService",
      "canRequestApprovedMovementService",
      "canRejectVesselCall",
      "canApprovePortService",
      "canRejectPortService",
      "canStartPortService",
      "canClosePortService",
      "canCancelPortService",
      "canWriteVesselCall",
      // Waste Disposal Garbage permissions
      "canApprovePortServicesWasteDisposalGarbage",
      "canRejectPortServicesWasteDisposalGarbage",
      "canApprovePortServicesWasteDisposalSludge",
      "canRejectPortServicesWasteDisposalSludge",
      "canApprovePortServicesWasteDisposalSlops",
      "canRejectPortServicesWasteDisposalSlops",
      "canApprovePortServicesShipToShip",
      "canRejectPortServicesShipToShip",
      "canSeeHyperlinkAssociatedPortService",
    ],
    isMarineDepartmentUser: true,
  };
};

const defineMarineSupervisorRole = (userPerm: UserPermissions) => {
  const { accessPermissionsList, actionPermissionsList } =
    getUserAccessAndActionPermissions(userPerm);

  const filteredActionPermissionsList = actionPermissionsList.filter(
    (perm) => perm !== "canUpdateVesselCall" && perm !== "canResumeVesselCall",
  );

  return {
    redirectPath: `${paths.vesselManagement.index}`,
    requestSCLabelKey: "security-clearance.add-security-clearance",
    registerVesselLabelKey: "vessel-management.register-vessel",
    requestGatepassLabelKey: "gatepass.request-gatepass",
    accessPermissionsList: [
      ...accessPermissionsList,
      "hasTaskAccess",
      "hasChangeVesselStatusAccess",
      "hasInternalNotesAccess",
      "hasShippingAgencyTransferAccess",
    ],
    customSecurityClearancePath: null,
    actionPermissionsList: [
      ...filteredActionPermissionsList,
      "canCloseMovementService",
      "canCancelMovementService",
      "canUpdateMovementService",
      "canRejectMovementService",
      "canRequestApprovedMovementService",
      "canRejectVesselCall",
      "canPreApproveVesselCall",
      "canClaimResumption",
      "canUnclaimResumption",
      "canApprovePortService",
      "canRejectPortService",
      "canStartPortService",
      "canClosePortService",
      "canCancelPortService",
      "canClaimShippingAgencyTransfer",
      "canUnclaimShippingAgencyTransfer",
      "canApproveShippingAgencyTransfer",
      "canRejectShippingAgencyTransfer",
      "canWriteVesselCall",
      "canApprovePortServicesWasteDisposalGarbage",
      "canRejectPortServicesWasteDisposalGarbage",
      "canApprovePortServicesWasteDisposalSludge",
      "canRejectPortServicesWasteDisposalSludge",
      "canApprovePortServicesWasteDisposalSlops",
      "canRejectPortServicesWasteDisposalSlops",
      "canApprovePortServicesShipToShip",
      "canRejectPortServicesShipToShip",
      "canSeeHyperlinkAssociatedPortService",
    ],
    isMarineDepartmentUser: true,
  };
};

const defineMarineOfficeRole = (userPerm: UserPermissions) => {
  const { accessPermissionsList, actionPermissionsList } =
    getUserAccessAndActionPermissions(userPerm);

  return {
    redirectPath: `${paths.vesselManagement.index}`,
    requestSCLabelKey: "security-clearance.add-security-clearance",
    registerVesselLabelKey: "vessel-management.register-vessel",
    requestGatepassLabelKey: "gatepass.request-gatepass",
    accessPermissionsList: [
      ...accessPermissionsList,
      "hasTaskAccess",
      "hasChangeVesselStatusAccess",
      "hasInternalNotesAccess",
      "hasShippingAgencyTransferAccess",
    ],
    customSecurityClearancePath: null,
    actionPermissionsList: [...actionPermissionsList],
  };
};

const defineHarbourMaterRole = (userPerm: UserPermissions) => {
  const { accessPermissionsList, actionPermissionsList } =
    getUserAccessAndActionPermissions(userPerm);
  const filteredActionPermissions = filterActionPermissions(actionPermissionsList, [
    "canInterruptVesselCall",
  ]);
  return {
    redirectPath: `${paths.vesselManagement.index}`,
    requestSCLabelKey: "security-clearance.add-security-clearance",
    registerVesselLabelKey: "vessel-management.register-vessel",
    requestGatepassLabelKey: "gatepass.request-gatepass",
    accessPermissionsList: [
      ...accessPermissionsList,
      "hasTaskAccess",
      "hasChangeVesselStatusAccess",
      "hasInternalNotesAccess",
      "hasShippingAgencyTransferAccess",
    ],
    customSecurityClearancePath: null,
    actionPermissionsList: [...filteredActionPermissions],
  };
};

const defineTerminalRole = (userPerm: UserPermissions, isOperationsUser: boolean) => {
  const { accessPermissionsList, actionPermissionsList } =
    getUserAccessAndActionPermissions(userPerm);
  const filteredAccessPermissions = accessPermissionsList.filter(
    (permission) => permission !== "hasVesselRegistrationAccess",
  );
  const filteredActionPermissions = filterActionPermissions(actionPermissionsList, [
    "canInterruptVesselCall",
  ]);
  const operationsPermissions = isOperationsUser
    ? [
        "canApprovePortServicesWasteDisposalGarbage",
        "canRejectPortServicesWasteDisposalGarbage",
        "canApprovePortServicesDebunkering",
        "canRejectPortServicesDebunkering",
        "canCancelPortService",
        "canApprovePortServicesShipChandling",
        "canRejectPortServicesShipChandling",
        "canStartPortServicesShipChandling",
        "canClosePortServicesShipChandling",
        "canUpdatePortServices",
      ]
    : [];

  return {
    redirectPath: `${paths.vesselCall.index}`,
    requestSCLabelKey: "security-clearance.add-security-clearance",
    registerVesselLabelKey: "vessel-management.register-vessel",
    requestGatepassLabelKey: "gatepass.request-gatepass",
    accessPermissionsList: [
      ...filteredAccessPermissions,
      "hasTaskAccess",
      "hasShippingAgencyTransferAccess",
    ],
    customSecurityClearancePath: null,
    actionPermissionsList: [
      ...filteredActionPermissions,
      "canNonDisputeMovementService",
      "canRejectMovementService",
      "canDisputeMovementService",
      "canApprovePortService",
      "canRejectPortService",
      "canStartPortService",
      "canClosePortService",
      "canRequestDispute",
      ...operationsPermissions,
    ],
    isOperationsUser,
  };
};

// Roles that identify an individual (UAE Pass / security applicant) SC applicant.
export const INDIVIDUAL_APPLICANT_ROLES = ["security-applicant", "uae-pass-user"] as const;
export const SECURITY_OPERATOR_ROLE = "security-operator" as const;
export const SECURITY_SUPERVISOR_ROLE = "security-supervisor" as const;

// Role keys we configure in {@link configureUserRolesParticularities}.
// Any role on the user that is NOT in this list is ignored (Keycloak realm).
// When adding a new app role to that map, add it here too.
export const APPLICATION_ROLE_KEYS = [
  "uae-pass-user",
  "security-applicant",
  "licensed-company-agent",
  "vendor-master",
  "security-operator",
  "security-supervisor",
  "shipping-agent",
  "marine-operator",
  "marine-supervisor",
  "marine-office",
  "harbour-master",
  "terminal",
  "service-desk",
  "managing-director-office",
  "hse",
] as const;

const APPLICATION_ROLE_KEY_SET = new Set<string>(APPLICATION_ROLE_KEYS);
const INDIVIDUAL_APPLICANT_ROLE_SET = new Set<string>(INDIVIDUAL_APPLICANT_ROLES);

export function isKnownApplicationRole(role: string): boolean {
  return APPLICATION_ROLE_KEY_SET.has(role);
}

export function isIndividualApplicantUser(roles: string[] | null | undefined): boolean {
  if (!Array.isArray(roles) || roles.length === 0) return false;
  return INDIVIDUAL_APPLICANT_ROLES.some((role) => roles.includes(role));
}

// True if the user has an app business role (vendor, sponsor, security dept etc.), not individual-only.
export function userHasNonIndividualApplicationRole(roles: string[] | null | undefined): boolean {
  if (!Array.isArray(roles)) return false;
  return roles.some(
    (role) => isKnownApplicationRole(role) && !INDIVIDUAL_APPLICANT_ROLE_SET.has(role),
  );
}

// Individual check-id / Emirates-ID bootstrap: has applicant role(s) and no configured business role.
// Unknown roles (offline_access, uma_authorization, or anything new from Keycloak) are ignored.
export function usesIndividualSecurityClearanceFlow(roles: string[] | null | undefined): boolean {
  if (!isIndividualApplicantUser(roles)) return false;
  return !userHasNonIndividualApplicationRole(roles);
}

export const configureUserRolesParticularities = (
  userPerm: UserPermissions,
  scID: string | null,
  scStatus: string | null,
  isOperationsUser = false,
  // When set (e.g. from login `/me`), used only for individual roles so previewing another SC row does not change request permissions.
  individualLinkScID?: string | null,
  individualLinkScStatus?: string | null,
): Record<string, RoleConfig> => {
  const indId = individualLinkScID !== undefined ? individualLinkScID : scID;
  const indStatus = individualLinkScStatus !== undefined ? individualLinkScStatus : scStatus;
  return {
    "uae-pass-user": defineIndividualRole(userPerm, indId, indStatus),
    "default-roles-pofpcs": null,

    "security-applicant": defineIndividualRole(userPerm, indId, indStatus),
    "licensed-company-agent": defineSponsorCompanyRole(userPerm),
    "vendor-master": defineVendorMasterRole(userPerm),
    "security-operator": defineSecurityOperatorRole(userPerm),
    "security-supervisor": defineSecuritySupervisorRole(userPerm),
    "shipping-agent": defineShippingAgentRole(userPerm),
    "marine-operator": defineMarineOperatorRole(userPerm),
    "marine-supervisor": defineMarineSupervisorRole(userPerm),
    "marine-office": defineMarineOfficeRole(userPerm),
    "harbour-master": defineHarbourMaterRole(userPerm),
    "terminal": defineTerminalRole(userPerm, isOperationsUser),
    "service-desk": defineServiceDeskRole(userPerm),
    "managing-director-office": defineManagingDirectorOfficeRole(userPerm),
    "hse": defineHSERole(userPerm),
  };
};

const Modules = {
  VESSEL_REGISTRATION: "vessel-registration",
  GATEPASS: "gatepass",
  SECURITY_CLEARANCE: "security-clearance",
  VESSEL_CALL: "vessel-call",
} as const;

const BE_TO_MODULE: Record<string, string> = {
  "vessel": Modules.VESSEL_REGISTRATION,
  "vessel-call": Modules.VESSEL_CALL,
  "security-clearance": Modules.SECURITY_CLEARANCE,
  "gatepass": Modules.GATEPASS,
};

function toPascalCase(s: string): string {
  return s
    .split(/[\s-_]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join("");
}

function filterActionPermissions(
  actionPermissionsList: string[],
  permissionsToExclude: string[],
): string[] {
  return actionPermissionsList.filter((permission) => !permissionsToExclude.includes(permission));
}

function getUserAccessAndActionPermissions(bePerms: UserPermissions): {
  accessPermissionsList: string[];
  actionPermissionsList: string[];
} {
  const accessPermissionsList: string[] = [];
  const actionPermissionsList: string[] = [];

  for (const [action, rawModules] of Object.entries(bePerms)) {
    // split the space-separated list of BE modules, ignore empties
    const modules = rawModules.split(/\s+/).filter(Boolean);

    for (const mod of modules) {
      const moduleKey = BE_TO_MODULE[mod] ?? mod;
      const ModulePascal = toPascalCase(moduleKey);

      if (action === "read") {
        accessPermissionsList.push(`has${ModulePascal}Access`);
        actionPermissionsList.push(`canPreview${ModulePascal}Request`);
      } else {
        const ActionPascal = toPascalCase(action);

        // all other actions produce action permissions
        if (action === "create") {
          accessPermissionsList.push(`has${ModulePascal}Access`);
          actionPermissionsList.push(`canRequest${ModulePascal}`);
          actionPermissionsList.push(`canRenew${ModulePascal}`);
        }

        if (action === "approve") {
          actionPermissionsList.push(`can${ActionPascal}${ModulePascal}`);
          actionPermissionsList.push(`canClaim${ModulePascal}`);
          actionPermissionsList.push(`canUnclaim${ModulePascal}`);
          actionPermissionsList.push(`canResubmit${ModulePascal}`);
        }

        if (action === "permanentlyreject") {
          actionPermissionsList.push(`canReject${ModulePascal}`);
        }

        if (action === "suspend") {
          actionPermissionsList.push(`can${ActionPascal}${ModulePascal}`);
        }

        if (action === "resume") {
          actionPermissionsList.push(`canUnsuspend${ModulePascal}`);
        }
        actionPermissionsList.push(`can${ActionPascal}${ModulePascal}`);
      }
    }
  }
  const filteredActionPermissionsList = actionPermissionsList.filter((p) => !p.includes("Create")); // 'create' action translates to 'Request'
  return {
    accessPermissionsList: [...new Set(accessPermissionsList)],
    actionPermissionsList: [...new Set(filteredActionPermissionsList)],
  };
}

export const checkPermission = (userActionPermissions: string[], permission: string) =>
  userActionPermissions.includes(permission);

type UserTypeFlags = {
  isOperationsUser: boolean;
  isMarineDepartmentUser: boolean;
  isShippingAgentUser: boolean;
  isHSEUser: boolean;
};

export const setUserType = ({
  isOperationsUser,
  isMarineDepartmentUser,
  isShippingAgentUser,
  isHSEUser,
}: UserTypeFlags) => {
  switch (true) {
    case isOperationsUser:
      return "Operations";
    case isMarineDepartmentUser:
      return "MD";
    case isShippingAgentUser:
      return "Shipping Agent";
    case isHSEUser:
      return "HSE";
    default:
      return "Other";
  }
};
